import { DocumentClassAttributes, MaterialAttributes, OrderAttributes, OrderDetailAttributes, SubRouteAttributes, VisitAttributes, WarehouseAttributes } from '@albatrosdeveloper/ave-models-package';
import { CommonModule, DecimalPipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DecimalConfigService } from 'app/services/decimal-config.service';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

// SmartGrid Imports
import {
  SmartGridComponent,
  ColDef,
  GridConfig,
  PageChangedEvent,
  SortChangedEvent,
  GridReadyEvent,
} from 'app/shared/components/smart-grid';
import { RouteUserCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/route-user-cell-renderer/route-user-cell-renderer.component';
import { DeliverySerialNavigationCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/delivery-serial-navigation-cell-renderer/delivery-serial-navigation-cell-renderer.component';
import { DateHourCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/date-hour-cell-renderer/date-hour-cell-renderer.component';
import { CustomerCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/customer-cell-renderer/customer-cell-renderer.component';
import { MaterialCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/material-cell-renderer/material-cell-renderer.component';
import { QuantityCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/quantity-cell-renderer/quantity-cell-renderer.component';
import { CurrencyCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/currency-cell-renderer/currency-cell-renderer.component';

import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { IPaginationResult, IRequestResult } from "app/common/interfaces";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { ExportOptions } from "app/layout/export-overlay";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { CompanyService } from "app/modules/components/configuration/company/company.service.service";
import { WarehouseService } from "app/modules/components/configuration/warehouse/warehouse.service";
import { BusinessPartnersFiltersComponent } from "app/modules/components/filters/business-partners-filters/business-partners-filters.component";
import { ConfigurationFiltersComponent } from "app/modules/components/filters/configuration-filters/configuration-filters.component";
import { DatesFiltersComponent } from "app/modules/components/filters/dates-filters/dates-filters.component";
import { DocumentsFiltersComponent } from "app/modules/components/filters/documents-filters/documents-filters.component";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";
import { MaterialsManagementFiltersComponent } from "app/modules/components/filters/materials-management-filters/materials-management-filters.component";
import { RoutesFiltersComponent } from "app/modules/components/filters/routes-filters/routes-filters.component";
import { StatusFiltersComponent } from "app/modules/components/filters/status-filters/status-filters.component";
import { MaterialService } from "app/modules/components/material/material/material.service";
import { SubRouteService } from "app/modules/components/routing/sub-routes/sub-route.service";
import { InvoicePdfOptions } from "app/modules/components/sales/invoice-list/Print/invoice-pdf-options.interface";
import { InvoiceService } from "app/modules/components/sales/invoice-list/Print/pdf-generator.service";
import { MasterUserInterface } from "app/modules/components/sales/order-generate/interfaces/master-user.interface";
import { OrdersListService } from "app/modules/components/sales/order-list-components/order-list.service";
import { NoteEditorComponent } from "app/modules/components/shared/note-editor/note-editor.component";
import { DocumentFlowButtonComponent } from "app/modules/shared/document-flow-button/document-flow-button.component";
import { DocumentFlowComponent } from "app/modules/shared/document-flow/document-flow.component";
import { DecimalFormatPipe } from "app/pipes/decimal-company.pipe";
import { FormatOtherDateHourPipe } from "app/pipes/other-date.pipe";
import { UserRouteDisplayComponent } from "app/shared/components/user-route-display/user-route-display.component";
import { UnitMeasureDisplayComponent } from "app/shared/components/unit-measure-display/unit-measure-display.component";
import { DateHelper } from "app/utils/date.helper";
import { getCode } from "app/utils/method";
import { environment } from "environments/environment.prod";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import {
  debounceTime,
  distinctUntilChanged,
  skip,
  Subject,
  Subscription,
  takeUntil,
} from "rxjs";
import { ExportDialogComponent } from "../../../../../layout/export-overlay/dialog/export-dialog.component";
import { OrderCustomerService } from "../order-customer/order-customer.service";
import {
  ORDER_INVOICE_EXPORT_ID,
  ORDER_INVOICE_TITLE,
} from "./exports/order-invoice.constant";

import { CompanyCurrencyPipe } from "app/pipes/currency.pipe";
import { FilterSectionComponent } from 'app/common/components/filter-section/filter-section.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { OrderStatusERPEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/order-status-erp.enum';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/status.enum';
import { BusinessPartnerCellRendererComponent } from 'app/modules/components/monitor/monitor-routes/cell-renderers/business-partner-cell-renderer.component';
import { TripLiquidationCellRendererComponent } from '../../liquidations/cell-renderers/trip-liquidation-cell-renderer.component';
import { TripCellRendererComponent } from '../../visits/visit-report/cell-renderers/trip-cell-renderer.component';
import { Router } from '@angular/router';
import { OrderCustomerExport } from '../order-customer/exports/order-customer.export';
import { OrderStatusCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/order-status-cell-redered/order-status-cell-redered.component';
@Component({
  selector: "app-order-customer",
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    SmartGridComponent, // Added for SmartGrid migration
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    HoverMenuDirective,
    MatSidenavModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    MatDialogModule,
    NgxMatSelectSearchModule,
    FormatOtherDateHourPipe,
    MatAutocompleteModule,
    MatRadioModule,
    MatCheckboxModule,
    MatChipsModule,
    FilterComponent,
    MatMenuModule,
    DecimalFormatPipe,
    DocumentFlowButtonComponent,
    StatusFiltersComponent,
    MaterialsManagementFiltersComponent,
    DatesFiltersComponent,
    ConfigurationFiltersComponent,
    DocumentsFiltersComponent,
    RoutesFiltersComponent,
    BusinessPartnersFiltersComponent,
    FilterSectionComponent,
    UserRouteDisplayComponent,
    UnitMeasureDisplayComponent,
    CompanyCurrencyPipe,
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    OrderCustomerExport,
    DecimalPipe,
  ],
  templateUrl: "./order-invoice.component.html",
  styleUrl: "./order-invoice.component.scss",
})
export class OrderInvoiceComponent implements OnInit, OnDestroy {
  isReprintLoading: { [key: string]: boolean } = {};
  getCode = getCode;
  private isInitialized = false;
  type = "DT005"; //Factura
  private decimalPipe = new DecimalPipe("en-US");
  displayedColumns: string[] = [
    "routeUser",
    "serials",
    "date",
    "shipToExternalKeyCode",
    "materialName",
    "soldQuantity",
    //"baseUnitOfMeasure",
    "unitPrice",
    "subTotal",
    "total",
    "actions",
  ];
  OrderStatusERPEnum = OrderStatusERPEnum;
  StatusEnum = StatusEnum;

  dataSource = new MatTableDataSource<OrderDetailAttributes>([]);

  // SmartGrid properties
  data: any[] = [];
  loading: boolean = false;

  totalItems: number = 0;
  searchControl = new FormControl("");
  search: string = "";
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof OrderDetailAttributes = "_id";
  sortDirection: "ASC" | "DESC" = "DESC";

  filter = {
    company: null,
    dateini: new Date(),
    dateend: new Date(),
    warehouseId: null,
    routes: [],
    item: null,
    status: null,
  };

  range = new FormGroup({
    start: new FormControl<Date | null>(new Date()),
    end: new FormControl<Date | null>(new Date()),
  });

  warehouseList: WarehouseAttributes[] = [];
  warehouseFilterList: WarehouseAttributes[] = [];
  searchWarehouse = "";

  subRouteList: SubRouteAttributes[] = [];
  listFilterSubRouteList: SubRouteAttributes[] = [];
  searchSubRoute = "";

  UMPActive: boolean;

  materialList: MaterialAttributes[] = [];
  searchMaterial = "";
  private searchSubject = new Subject<string>();

  // selectedDocumentClass: string = '0';
  selectedDocumentClass: string[] = [];
  filteredDocumentClasses: Subject<DocumentClassAttributes[]> = new Subject();
  documentClassMultiFilterCtrl: FormControl = new FormControl();
  isIndeterminateDocumentClass: boolean = false;
  isCheckedDocumentClass: boolean = false;
  private _onDestroyDocumentClass = new Subject<void>();

  selectedSeller: string = "0";
  selectedWithTravel: string[] = [];

  documentClassesList: DocumentClassAttributes[] = [];
  sellerList: MasterUserInterface[] = [];

  selectedSoldTo: string = "0";
  soldToControl = new FormControl("");
  soldToList: any[] = [];
  soldToFilterSelected: any = null;

  selectedShipTo: string = "0";
  shipToControl = new FormControl("");
  shipToList: any[] = [];

  statusAnuladas = false; // Corresponde a 0
  statusActivas = false; // Corresponde a 1

  // Variables for direct sale filtering, both true by default to get all records.
  isDirectSale = true;
  isNotDirectSale = true;

  // Valor que se enviará (0, 1 o null)
  selectedStatus: number | null = null;

  //BUSCADOR
  searchSubscription: Subscription;

  form: FormGroup;

  //Para filtros
  selectedStatuses: number[] = [];

  // Estados ERP
  selectedERPStatuses: number[] = [];

  selectedCustomerType: string[] = [];

  loadConfigData = false;
  loadDocumentsData = false;
  loadRoutesData = false;
  loadBusinessPartnersData = false;
  loadMaterialsData = false;

  // SmartGrid Configuration
  columnDefs: ColDef<any>[] = [];
  gridConfig: GridConfig<any> = {
    rowIdField: "_id" as any,
    emptyState: {
      message: "No encontramos tus resultados!",
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: "api",
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: "api",
      multiColumn: false,
      defaultSort: [{ field: "_id" as any, direction: "desc" }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
    },
    columnVisibility: {
      enabled: false, // Disabled - now in settings panel
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40, // Increased for more spacing
    },
    styling: {
      maxHeight: "85vh",
      minHeight: "80vh",
      density: "comfortable",
      fontSize: "12px",
      cellPaddingX: "3px",
      cellPaddingY: "3px",
    },
    settingsPanel: {
      enabled: true,
      position: "right",
      enableColumnVisibility: true,
      defaultDensity: "compact",
    },
  };

  constructor(
    private orderCustomer: OrderCustomerService,
    private readonly warehouseService: WarehouseService,
    private readonly subRouteService: SubRouteService,
    private readonly companyService: CompanyService,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private orderCustomerExport: OrderCustomerExport,
    private ordersListService: OrdersListService,
    private readonly cdr: ChangeDetectorRef,
    private readonly materialService: MaterialService,
    private readonly searchService: SearchService,
    private invoiceService: InvoiceService,
    private fb: FormBuilder,
    private companyCurrencyPipe: CompanyCurrencyPipe,
    private decimalConfigService: DecimalConfigService,
    private readonly router: Router
  ) {
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      startTrip: [],
      endTrip: [],
      companyId: [],
      salesOfficeId: [],
      documentClassId: [],
      subRouteId: [],
      assignedUserId: [],
      payingBusinessPartnerId: [],
      receivingBusinessPartnerId: [],
      warehouseId: [],
      end_startTrip: [],
      end_endTrip: [],
      soldToControl: [],
      searchReceivingBusinessPartner: [],
      searchAssignedUser: [],
      materialGroupId: [[]],
      materialId: [],
      pickerUpload_start: [],
      pickerUpload_end: []
    });
  }

  onConfigExpanded(): void {
    this.loadConfigData = true;
  }

  onDocumentsExpanded(): void {
    this.loadDocumentsData = true;
  }

  onRoutesExpanded(): void {
    this.loadRoutesData = true;
  }

  onBusinessPartnersExpanded(): void {
    this.loadBusinessPartnersData = true;
  }

  onMaterialsExpanded(): void {
    this.loadMaterialsData = true;
  }

  ngOnInit(): void {
    this.initializeColumnDefs();
    this.onHandleSearch();
    this.fetchCompanies();
    this.onFilter();
    this.isInitialized = true;
  }

  private initializeColumnDefs(): void {
    this.columnDefs = [
      {
        field: "subRoute.code" as any,
        headerName: "Ruta - Usuario",
        minWidth: 300,
        sortable: true,
        pinned: "left",
        cellRenderer: RouteUserCellRendererComponent,
        filterOptions: {
          valueExtractor: (item: any) => item.subRoute?.name,
          customFilterFn(rowData, filterValue) {
            const subRouteName = rowData.subRoute?.name;
            if (!subRouteName) return !filterValue;
            return subRouteName === filterValue;
          },
        },
      },          
      {
        field: 'trip.id',
        headerName: 'Viaje',
        minWidth: 150,
        sortable: true,
        cellRenderer: TripCellRendererComponent,
        cellRendererParams: {
          onTripClick: (data: any) => this.goToTravel(data)
        }
      },
      {
        field: "serials",
        headerName: "Nro. Doc.",
        minWidth: 160,
        sortable: true,
        cellRenderer: DeliverySerialNavigationCellRendererComponent,
      },
      {
        field: "date",
        headerName: "Fecha",
        minWidth: 140,
        sortable: true,
        cellRenderer: DateHourCellRendererComponent,
        filterOptions: {
          valueExtractor(item) {
            if (!item.date) return "";
            const date = new Date(item.date);
            return date.toLocaleDateString("es-ES", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            });
          },
          customFilterFn(rowData, filterValue) {
            const rowDate = rowData.date;
            if (!rowDate) return !filterValue;
            const rowDateObj = new Date(rowDate);
            const rowDateStr = rowDateObj.toLocaleDateString("es-ES", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            });
            return rowDateStr === filterValue;
          },
        },
      },
      {
        field: "soldTo.name",
        headerName: "Pagador/Receptor",
        cellRenderer: BusinessPartnerCellRendererComponent,
        autoSize: true,
        minWidth: 250, // Much wider for code + long name
        sortable: true,
        filterOptions: {
          // Display customer name in filter dropdown instead of code
          // Note: Requires shipToName field to be present in row data
          valueExtractor: (item: any) => item?.soldTo?.name,
          customFilterFn(rowData, filterValue) {
            const rowCustomer = rowData?.soldTo?.name;
            if (!rowCustomer) return !filterValue;

            return rowCustomer === filterValue;
          },
        },
      },      
      {
        field: "statusId" as any,
        headerName: "Anulada",
        minWidth: 200,
        sortable: true,
        cellRenderer: OrderStatusCellRendererComponent
      },
      {
        field: "materialName.name" as any,
        headerName: "Material",
        minWidth: 200,
        sortable: true,
        cellRenderer: MaterialCellRendererComponent,
        filterOptions: {
          valueExtractor: (item: any) => item.materialName?.name,
          customFilterFn(rowData, filterValue) {
            const rowMaterial = rowData.materialName?.name;
            if (!rowMaterial) return !filterValue;
            return rowMaterial === filterValue;
          },
        },
      },
      {
        field: "soldQuantity",
        headerName: "Cantidad (U.M)",
        minWidth: 180,
        sortable: true,
        cellRenderer: QuantityCellRendererComponent,
        cellRendererParams: {
          getUMPActive: () => this.UMPActive, // Arrow function para valor dinámico
        },
      },
      {
        field: "unitPrice",
        headerName: "Precio",
        minWidth: 120,
        sortable: true,
        cellClass: "text-right",
        cellRenderer: CurrencyCellRendererComponent,
        filterOptions: this.createCurrencyFilterOptions("unitPrice"),
      },
      {
        field: "subTotal",
        headerName: "Subtotal",
        minWidth: 120,
        sortable: true,
        cellClass: "text-right",
        cellRenderer: CurrencyCellRendererComponent,
        filterOptions: this.createCurrencyFilterOptions("subTotal"),
      },
      {
        field: "total",
        headerName: "Total",
        minWidth: 120,
        sortable: true,
        cellClass: "text-right",
        cellRenderer: CurrencyCellRendererComponent,
        filterOptions: this.createCurrencyFilterOptions("total"),
      },
      {
        colId: "actions",
        headerName: "",
        minWidth: 80,
        maxWidth: 80,
        sortable: false,
        pinned: "right",
        // Note: Actions column with custom invoice actions (note, reprint) requires special handling
        // Keeping as simple field for now - may need custom cell renderer
      },
    ];
  }

  ngAfterViewInit(): void {
    this.documentClassMultiFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroyDocumentClass))
      .subscribe(() => {
        this.filterDocumentClasses();
        this.setToggleAllCheckboxStateDocumentClass();
      });

    this.searchControl.valueChanges
      .pipe(skip(1), debounceTime(500), distinctUntilChanged())
      .subscribe((filterValue: string) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = filterValue;
          this.onFilter();
        }
      });

    this.soldToControl.valueChanges
      .pipe(skip(1), debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        if (this.isInitialized) {
          if (
            typeof value === "string" &&
            value.trim().length === 24 &&
            /^[a-f0-9]{24}$/.test(value)
          ) {
            return;
          }
          this.soldToList = [];
          this.soldToFilterSelected = null;
          this.getListSoldTo(value);

          this.getListReport();
        }
      });

    this.shipToControl.valueChanges
      .pipe(skip(1), debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        if (this.isInitialized) {
          if (
            typeof value === "string" &&
            value.trim().length === 24 &&
            /^[a-f0-9]{24}$/.test(value)
          ) {
            return;
          }

          this.shipToList = [];
          this.getListShipTo(value);
          this.getListReport();
        }
      });

    this.setupMaterialSearchControl();
    this.getMaterialList();
  }

  /**
   * Format a number as currency with symbol and thousands separators
   * Example: 1250.5 -> "L 1,250.50"
   */
  private formatCurrency(value: number): string {
    if (value == null) return "-";

    const decimals = this.decimalConfigService.getDecimals();
    const formattedNum =
      this.decimalPipe.transform(value, `1.${decimals}-${decimals}`) ?? "-";

    if (formattedNum === "-") return "-";

    const symbol = this.companyCurrencyPipe.transform("symbol");
    return `${symbol} ${formattedNum}`;
  }

  /**
   * Create filterOptions for monetary columns
   * Reusable for unitPrice, subTotal, total
   */
  private createCurrencyFilterOptions(fieldPath: string) {
    return {
      valueExtractor: (item: any) => {
        const value = item[fieldPath];
        return this.formatCurrency(value);
      },
      customFilterFn: (rowData: any, filterValue: string) => {
        const value = rowData[fieldPath];
        const formattedValue = this.formatCurrency(value);
        if (!formattedValue || formattedValue === "-") return !filterValue;
        return formattedValue === filterValue;
      },
    };
  }

  private fetchDocumentClass(): void {
    let dataToFilter = {
      type: this.type,
    };
    // this.ordersListService.fetchDocumentClasses(dataToFilter).subscribe((res) => {
    //   this.documentClassesList = res.data;
    // })
    this.ordersListService
      .fetchDocumentClasses(dataToFilter)
      .subscribe((res) => {
        this.documentClassesList = res.data;
        this.filteredDocumentClasses.next(this.documentClassesList);
      });
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .pipe(skip(1), debounceTime(300))
      .subscribe((term) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = term;
          this.onFilter();
        }
      });
  }

  onFilter() {
    this.range.valueChanges.subscribe((rangeValues) => {
      this.filter.dateini = rangeValues.start || this.filter.dateini;
      this.filter.dateend = rangeValues.end || this.filter.dateend;
    });

    this.getListReport();
  }

  private filterDocumentClasses(): void {
    if (!this.documentClassesList) {
      return;
    }
    let search = this.documentClassMultiFilterCtrl.value;
    if (!search) {
      this.filteredDocumentClasses.next(this.documentClassesList);
      return;
    } else {
      search = search.toLowerCase();
    }
    const filtered = this.documentClassesList.filter((docClass) =>
      docClass.name.toLowerCase().includes(search)
    );
    this.filteredDocumentClasses.next(filtered);
  }

  private setToggleAllCheckboxStateDocumentClass(): void {
    const selectedCount = this.selectedDocumentClass.length;
    const totalCount = this.documentClassesList.length;

    this.isCheckedDocumentClass =
      selectedCount > 0 && selectedCount === totalCount;
    this.isIndeterminateDocumentClass =
      selectedCount > 0 && selectedCount < totalCount;
  }

  onClearFilter() {
    this.filter = {
      company: this.filter.company,
      dateini: new Date(),
      dateend: new Date(),
      warehouseId: null,
      routes: [],
      item: null,
      status: null,
    };
    this.selectedDocumentClass = null;
    this.filter.status = null;
    this.range.reset({
      start: new Date(),
      end: new Date(),
    });
    const today = new Date();
    this.form.reset({ start: today, end: today });
    this.onFilter();
  }

  applyFilter(filterValue: string) {
    this.pageIndex = 1;
    this.search = filterValue;
    this.getListReport();
  }

  // SmartGrid Event Handlers
  onPageChanged(event: PageChangedEvent): void {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.getListReport();
  }

  onSortChanged(event: SortChangedEvent<any>): void {
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as keyof OrderDetailAttributes;
      this.sortDirection = sortState.direction === "asc" ? "ASC" : "DESC";
    } else {
      this.sortActive = "_id";
      this.sortDirection = "DESC";
    }
    this.getListReport();
  }

  onGridReady(event: GridReadyEvent<any>): void {
    // Can be used for additional initialization if needed
  }

  // Keep old methods for backward compatibility
  changeSort(e) {
    this.sortActive = e.active;
    this.sortDirection = e.direction?.toUpperCase() || "ASC";
    this.getListReport();
  }

  changePage(e) {
    this.pageIndex = e.pageIndex + 1;
    this.pageSize = e.pageSize;
    this.getListReport();
  }

  getListReport() {
    this.loading = true; // Start loading
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sort: this.getSortField(),
      sortDirection: this.sortDirection,
    };

    const adjustedStart = new Date(this.filter.dateini);
    adjustedStart.setHours(0, 0, 0, 0); // 00:00:00.000

    const adjustedEnd = new Date(this.filter.dateend);
    adjustedEnd.setHours(23, 59, 59, 999); // 23:59:59.999

    // isDirectSale: this.isDirectSale != this.isNotDirectSale
    //   ? this.isDirectSale
    //   : null,

    let filter: IFilters = {
      type: this.type,
      startDate: DateHelper.getStartOfDay(this.form.get("start")?.value),
      endDate:
        DateHelper.getEndOfDay(this.form.get("end")?.value) ??
        DateHelper.getEndOfDay(this.form.get("start")?.value) ??
        null,
      startTrip: DateHelper.getStartOfDay(this.form.get("startTrip")?.value),
      endTrip:
        DateHelper.getEndOfDay(this.form.get("endTrip")?.value) ??
        DateHelper.getEndOfDay(this.form.get("startTrip")?.value) ??
        null,
      end_startTrip: DateHelper.getStartOfDay(
        this.form.get("end_startTrip")?.value
      ),
      end_endTrip:
        DateHelper.getEndOfDay(this.form.get("end_endTrip")?.value) ??
        DateHelper.getEndOfDay(this.form.get("end_startTrip")?.value) ??
        null,
      pickerUpload_start: DateHelper.getStartOfDay(this.form.get("pickerUpload_start")?.value), 
      pickerUpload_end: DateHelper.getEndOfDay(this.form.get("pickerUpload_end")?.value) ??
        DateHelper.getEndOfDay(this.form.get("pickerUpload_start")?.value) ??
        null,
      companyId: this.form.get("companyId")?.value,
      salesOfficeId: this.form.get("salesOfficeId")?.value,
      warehouseId: this.form.get("warehouseId")?.value, //
      documentClass: this.form.get("documentClassId")?.value,
      subRouteId: this.form.get("subRouteId")?.value, //
      soldTo: this.form.get("payingBusinessPartnerId")?.value,
      shipTo: this.form.get("receivingBusinessPartnerId")?.value,
      seller: this.form.get("assignedUserId")?.value,
      status: this.selectedStatuses ? this.selectedStatuses : null,
      statusERP: this.selectedERPStatuses.length
        ? this.selectedERPStatuses
        : null,
      typeCustomer: this.selectedCustomerType.length
        ? this.selectedCustomerType
        : null,
      materialGroupId: this.form.get("materialGroupId").value,
      materialId: this.form.get("materialId").value,
      isDirectSale:
        this.isDirectSale != this.isNotDirectSale ? this.isDirectSale : null,
      ordersToFilter: null,
    };

    this.orderCustomer.findAllWithCount(options, filter).subscribe({
      next: (res: IPaginationResult<OrderDetailAttributes>) => {
        this.dataSource.data = res?.data?.[0]?.data ?? []; // Keep for backward compatibility
        this.data = res?.data?.[0]?.data ?? []; // For SmartGrid
        this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.loading = false; // Stop loading
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener reporte",
          "Cerrar",
          { duration: 5000 }
        );
        this.loading = false; // Stop loading on error
      },
    });
  }

  private getSortField(): keyof OrderDetailAttributes {

    return this.sortActive;
  }

  //filter
  private fetchCompanies(): void {
    this.companyService.findToComboBox().subscribe((res) => {
      if (res.data.length === 1) {
        this.UMPActive = res.data[0].UMPActive;
      }
    });
  }

  private fetchAllWarehouses(): void {
    this.warehouseService.findToComboBox().subscribe({
      next: (res) => {
        this.warehouseList = res.data;
        this.warehouseFilterList = this.warehouseList.slice();
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error fetching warehouses",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  goToTravel(element: VisitAttributes) {
        if (element.trip?.id) {
          const queryParams = { tripId: element.trip.id };
          const urlTree = this.router.createUrlTree(['monitor', 'principal-screen'], { queryParams });
          const absoluteUrl = window.location.origin + this.router.serializeUrl(urlTree);
          window.open(absoluteUrl, '_blank');
        }
      }
      
  changueOptionsWarehouse(event) {
    if (!this.warehouseList) {
      return;
    }
    let search = event;
    if (!search) {
      this.warehouseFilterList = this.warehouseList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.warehouseFilterList = this.warehouseList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  changeWarehouse(event): void {
    this.subRouteList = [];
    this.listFilterSubRouteList = [];
    this.getAllRutas();
  }

  private getAllRutas(): void {
    this.subRouteService
      .findToWarehouse(this.filter.warehouseId ? [this.filter.warehouseId] : [])
      .subscribe({
        next: (res) => {
          this.subRouteList = res.data;
          this.listFilterSubRouteList = this.subRouteList.slice();
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error fetching route",
            "Cerrar",
            { duration: 5000 }
          );
        },
      });
  }

  changueOptionsSubRoute(event) {
    if (!this.subRouteList) {
      return;
    }
    let search = event;
    if (!search) {
      this.listFilterSubRouteList = this.subRouteList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.listFilterSubRouteList = this.subRouteList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  onExportButtonPressed() {
    if (!this.orderCustomerExport.canStartTask()) return;
    const dialogRef = this.matDialog.open(ExportDialogComponent, {
      data: { title: ORDER_INVOICE_TITLE },
    });
    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.orderCustomerExport.export(
          options.format,
          options.range,
          this.totalItems,
          this.pageSize,
          this.pageIndex,
          ORDER_INVOICE_EXPORT_ID,
          ORDER_INVOICE_TITLE
        );
      }
    });
  }

  onDocumentClassSelectionChange(selectedValues: string[]): void {
    this.selectedDocumentClass = selectedValues;
  }

  private getListSeller() {
    this.ordersListService.fetchMasterUsers().subscribe({
      next: (res: IRequestResult<MasterUserInterface[]>) => {
        if (res.status == "success") {
          this.sellerList = res.data;
        }
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error fetching sellers",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  getListSoldTo(search?): void {
    let dataToSend = {
      companyIds: [environment.companyId],
      search: search?.toString() || "",
    };
    this.ordersListService.fetchSoldTos(dataToSend).subscribe((res) => {
      this.soldToList = res?.data;
      this.cdr.detectChanges();
    });
  }

  onChangeSoldTo() {
    this.soldToFilterSelected = this.soldToList.find(
      (stl) => stl.id == this.soldToControl.value
    );
    this.shipToControl.setValue("");
    this.getListShipTo();
  }

  getListShipTo(search?): void {
    this.shipToList = [];
    const soldToValue = this.soldToFilterSelected
      ? this.soldToFilterSelected._id
      : null;

    let dataToSend: any = {
      companyIds: [environment.companyId],
      search: search?.toString() || "",
    };

    if (soldToValue) {
      dataToSend.soldToMongoId = soldToValue;
    }

    this.ordersListService.fetchShipTos(dataToSend).subscribe((res) => {
      this.shipToList = res?.data;
      this.cdr.detectChanges();
    });
  }

  fetchShipToName = (shipToId: string): string => {
    const foundShipTo = this.shipToList.find(
      (shipTo) => shipTo.id === shipToId
    );
    if (foundShipTo) {
      return foundShipTo.name;
    }
    return "";
  };

  fetchSoldToName = (soldtoId: string): string => {
    const foundSoldTo = this.soldToList.find(
      (soldTo) => soldTo.id === soldtoId
    );
    if (foundSoldTo) {
      return foundSoldTo.name;
    }
    return "";
  };

  changueOptionsMaterial(event) {
    this.searchSubject.next(event);
  }

  private setupMaterialSearchControl(): void {
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm) => {
        if (searchTerm) {
          this.searchMaterial = searchTerm;
          this.getMaterialList();
        }
      });
  }

  private getMaterialList(): void {
    const materialOptions = {
      search: this.searchMaterial ? this.searchMaterial : null,
      currentPage: 1,
      limitPerPage: 50,
      sortActive: "name",
      //sortDirection: "ASC"
    };

    this.materialService
      .findAllWithCount(
        materialOptions,
        this.filter.company ? this.filter.company : []
      )
      .subscribe({
        next: (res: IPaginationResult<MaterialAttributes>) => {
          this.materialList = res?.data?.[0]?.data ?? [];
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al traer registros",
            "Cerrar",
            { duration: 3000 }
          );
        },
      });
  }

  updateStatus(status: number, isChecked: boolean): void {
    if (status === 0) {
      this.statusAnuladas = isChecked;
    } else if (status === 1) {
      this.statusActivas = isChecked;
    }

    if (this.statusAnuladas && !this.statusActivas) {
      this.filter.status = 0;
    } else if (!this.statusAnuladas && this.statusActivas) {
      this.filter.status = 1;
    } else {
      this.filter.status = null;
    }
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  openFlowModal(element) {
    this.matDialog.open(DocumentFlowComponent, {
      panelClass: "form-modal-flow",
      width: "95%",
      data: element,
    });
  }

  checkIfDirectSale(check: number, isChecked: boolean) {
    if (check == 0) {
      // 0 for direct sale.
      this.isDirectSale = isChecked;
    } else if (check == 1) {
      // 1 for not direct sale.
      this.isNotDirectSale = isChecked;
    }

    // If both are not selected, we set the other one to true:
    if (!this.isDirectSale && !this.isNotDirectSale) {
      if (check == 0) {
        this.isNotDirectSale = true;
      } else if (check == 1) {
        this.isDirectSale = true;
      }
    }
  }

  openNoteDialog(element: OrderAttributes) {
    const dialogRef = this.matDialog.open(NoteEditorComponent, {
      width: "400px",
      data: element,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const updateData = {
          note: result,
        };
        this.ordersListService
          .updateNoteInvoice(element._id.toString(), updateData)
          .subscribe({
            next: (res) => {
              this.snackBar.open("Nota actualizada correctamente", "Cerrar", {
                duration: 3000,
              });
            },
            error: (err) => {
              this.snackBar.open(
                err.error.message ||
                  "Algo salio mal... No se actualizo la nota.",
                "Cerrar",
                { duration: 5000 }
              );
            },
          });
      }
    });
  }

  reprintInvoice(element: OrderDetailAttributes): void {
    if (!element._id) {
      this.snackBar.open("No se encontró el ID de la factura", "Cerrar", {
        duration: 3000,
      });
      return;
    }
    const invoiceId = element._id.toString();
    this.isReprintLoading[invoiceId] = true;
    const options: InvoicePdfOptions = {
      format: "letter",
      landscape: false,
      scale: 1,
      printBackground: true,
      marginTop: "10mm",
      marginBottom: "10mm",
    };
    this.invoiceService.downloadAndSaveInvoice(invoiceId, options).subscribe({
      next: () => {
        this.snackBar.open("Factura descargada correctamente", "Cerrar", {
          duration: 3000,
        });
      },
      error: (error) => {
        this.snackBar.open(
          error.message || "Error al imprimir la factura",
          "Cerrar",
          { duration: 5000 }
        );
      },
      complete: () => {
        this.isReprintLoading[invoiceId] = false;
      },
    });
  }
  canReprintInvoice(element: OrderDetailAttributes): boolean {
    return this.invoiceService.canReprintInvoice(element);
  }
}
